import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { automotiveTools } from '../../../../mocks/automotive-tools';
import { automotiveReviews } from '../../../../mocks/automotive-reviews';
import PageMeta from '../../../../components/base/PageMeta';
import JsonLd from '../../../../components/base/JsonLd';
import BrandLogo from '../../../../components/base/BrandLogo';
import { getReviewAvatar } from '../../../../utils/reviewAvatar';
import SiteFooter from '../../../../components/feature/SiteFooter';

export default function AutomotiveTransportationDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'features' | 'pricing'>('overview');
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [helpfulVotes, setHelpfulVotes] = useState<Record<number, boolean>>({});
  const langRef = useRef<HTMLDivElement>(null);

  const tool = automotiveTools.find((t) => t.slug === slug);
  const reviews = automotiveReviews[slug ?? ''] ?? [];
  const relatedTools = automotiveTools.filter((t) => tool?.relatedSlugs.includes(t.slug));
  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderStars = (rating: number) =>
    Array.from({ length: 5 }).map((_, i) => {
      if (i < Math.floor(rating)) return <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>;
      if (i === Math.floor(rating) && rating % 1 >= 0.5) return <i key={i} className="ri-star-half-line text-amber-400 text-sm"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-sm"></i>;
    });

  if (!tool) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl font-black text-gray-200 mb-4">404</div>
          <p className="text-gray-500 mb-4">Service not found</p>
          <a href="/categories/automotive-transportation" className="text-slate-700 font-semibold hover:underline">← Back to Automotive &amp; Transportation</a>
        </div>
      </div>
    );
  }

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Review',
        name: `${tool.name} Review 2026 – ${tool.tagline}`,
        url: `${siteUrl}/categories/automotive-transportation/${tool.slug}`,
        itemReviewed: {
          '@type': 'Product',
          name: tool.name,
          description: tool.tagline,
          category: tool.quickInfo.category,
        },
        reviewRating: { '@type': 'Rating', ratingValue: tool.rating, bestRating: 5 },
        author: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
        publisher: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
          { '@type': 'ListItem', position: 3, name: 'Automotive & Transportation', item: `${siteUrl}/categories/automotive-transportation` },
          { '@type': 'ListItem', position: 4, name: tool.name, item: `${siteUrl}/categories/automotive-transportation/${tool.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title={`${tool.name} Review 2026 – ${tool.tagline} | 247digitrend`}
        description={`${tool.intro[0].substring(0, 155)}...`}
        canonical={`${siteUrl}/categories/automotive-transportation/${tool.slug}`}
        keywords={`${tool.name} review, ${(tool.tags ?? []).join(', ')}, best automotive transportation services 2026`}
        ogType="article"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* SIGN UP MODAL */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-slate-100">
                  <i className="ri-check-line text-slate-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247digitrend.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-slate-700 to-gray-800 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-slate-700 to-gray-800">
                    <i className="ri-car-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247digitrend</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-slate-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-slate-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-slate-700 to-gray-800 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors">Home</a>
            <a href="/categories" className="text-[15px] font-semibold text-slate-800 transition-colors">Categories</a>
            <a href="/reviews" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors">Reviews</a>
            <a href="/comparisons" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors">Comparisons</a>
            <a href="/blog" className="text-[15px] font-medium text-gray-700 hover:text-slate-800 transition-colors">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-slate-50 text-slate-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-slate-700 to-gray-800 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">Sign Up</button>
          </div>
        </div>
      </header>

      {/* BREADCRUMB */}
      <div className="pt-36 bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-8 py-3 flex items-center gap-2 text-xs text-gray-500">
          <a href="/" className="hover:text-slate-700 transition-colors cursor-pointer">Home</a>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <a href="/categories" className="hover:text-slate-700 transition-colors cursor-pointer">Categories</a>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <a href="/categories/automotive-transportation" className="hover:text-slate-700 transition-colors cursor-pointer">Automotive &amp; Transportation</a>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-gray-800 font-semibold truncate max-w-32">{tool.name}</span>
        </div>
      </div>

      {/* TOOL HERO HEADER */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <div className="flex items-start gap-5">
            <BrandLogo logoUrl={tool.logoUrl} logoGrad={tool.logoGrad} logoIcon={tool.logoIcon} name={tool.name} size="lg" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-1">
                <h1 className="text-2xl font-black text-gray-900">{tool.name}</h1>
                <span className="text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-0.5">{tool.priceShort}</span>
              </div>
              <p className="text-sm text-gray-500 mb-3">{tool.tagline}</p>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-0.5">{renderStars(tool.rating)}</div>
                  <span className="text-sm font-bold text-gray-800">{tool.rating}</span>
                  <span className="text-xs text-gray-400">({tool.reviewCount.toLocaleString()} reviews)</span>
                </div>
                <span className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-full px-3 py-1">
                  <i className="ri-price-tag-3-line mr-1"></i>{tool.price}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 bg-gray-900 hover:bg-gray-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-external-link-line text-sm"></i>
                Visit website
              </a>
              <button className="flex items-center gap-2 border border-gray-200 hover:border-slate-300 text-gray-600 hover:text-slate-700 text-sm font-semibold px-4 py-2.5 rounded-xl cursor-pointer transition-all whitespace-nowrap">
                <i className="ri-bookmark-line text-sm"></i>
                Save
              </button>
            </div>
          </div>

          {/* TABS */}
          <div className="flex items-center gap-0 mt-6 border-b border-gray-100">
            {(['overview', 'features', 'pricing'] as const).map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`px-5 py-2.5 text-sm font-semibold capitalize transition-all cursor-pointer border-b-2 -mb-px ${activeTab === tab ? 'border-slate-700 text-slate-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-6xl mx-auto px-8 py-10">
        <div className="grid grid-cols-12 gap-8">

          {/* LEFT COLUMN */}
          <div className="col-span-8">

            {activeTab === 'overview' && (
              <>
                {/* Hero image */}
                <div className="w-full h-64 rounded-2xl overflow-hidden mb-8">
                  <img src={tool.image} alt={tool.name} className="w-full h-full object-cover object-top" />
                </div>

                {/* Introduction */}
                <div className="mb-8">
                  <h2 className="text-lg font-black text-gray-900 mb-4">Introduction</h2>
                  <div className="space-y-4">
                    {tool.intro.map((p, i) => (
                      <p key={i} className="text-sm text-gray-600 leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>

                {/* Affiliate Disclosure */}
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 mb-8">
                  <p className="text-xs text-amber-800 flex items-start gap-2">
                    <i className="ri-information-line text-amber-600 text-sm mt-0.5 flex-shrink-0"></i>
                    <span><strong>Affiliate Disclosure:</strong> This review contains affiliate links. If you purchase through these links, we may earn a commission at no extra cost to you. Our reviews are always honest and independent.</span>
                  </p>
                </div>

                {/* Pros & Cons */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
                    <h3 className="text-sm font-black text-emerald-800 mb-3 flex items-center gap-2">
                      <i className="ri-thumb-up-line text-emerald-600"></i> Pros
                    </h3>
                    <ul className="space-y-2">
                      {tool.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-emerald-700">
                          <i className="ri-check-line text-emerald-500 text-sm mt-0.5 flex-shrink-0"></i>
                          <span>{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
                    <h3 className="text-sm font-black text-red-800 mb-3 flex items-center gap-2">
                      <i className="ri-thumb-down-line text-red-500"></i> Cons
                    </h3>
                    <ul className="space-y-2">
                      {tool.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-red-700">
                          <i className="ri-close-line text-red-400 text-sm mt-0.5 flex-shrink-0"></i>
                          <span>{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* User Reviews */}
                <div>
                  <h2 className="text-lg font-black text-gray-900 mb-5">User Reviews</h2>
                  <div className="space-y-4">
                    {reviews.map((r, i) => (
                      <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5" style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <img src={getReviewAvatar(r.name)} alt={r.name} className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-900">{r.name}</span>
                                {r.badges.map((b, bi) => (
                                  <span key={bi} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${b === 'Verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{b}</span>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <div className="flex items-center gap-0.5">{renderStars(r.rating)}</div>
                                <span className="text-xs text-gray-400">{r.date}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed mb-3">{r.text}</p>
                        <div className="flex items-center gap-4">
                          <button onClick={() => setHelpfulVotes((prev) => ({ ...prev, [i]: !prev[i] }))} className={`flex items-center gap-1.5 text-xs font-semibold cursor-pointer transition-colors ${helpfulVotes[i] ? 'text-slate-700' : 'text-gray-400 hover:text-gray-700'}`}>
                            <i className="ri-thumb-up-line text-sm"></i>
                            Helpful ({helpfulVotes[i] ? r.helpful + 1 : r.helpful})
                          </button>
                          <button className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer">Report</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'features' && (
              <div>
                <div className="w-full h-64 rounded-2xl overflow-hidden mb-8">
                  <img src={tool.image} alt={tool.name} className="w-full h-full object-cover object-top" />
                </div>
                <h2 className="text-lg font-black text-gray-900 mb-6">Key Features</h2>
                <div className="grid grid-cols-2 gap-4">
                  {tool.pros.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${tool.logoGrad} flex-shrink-0`}>
                        <i className={`${tool.logoIcon} text-white text-sm`}></i>
                      </div>
                      <p className="text-xs text-gray-700 leading-relaxed">{feature}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 bg-slate-50 rounded-2xl p-6 border border-slate-100">
                  <h3 className="text-sm font-black text-slate-900 mb-2">Additional Information</h3>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    {tool.name} offers comprehensive capabilities in its category. Visit the official website to explore the complete feature set, specifications, and service details.
                  </p>
                  <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-slate-600 hover:text-slate-800 cursor-pointer">
                    Explore {tool.name} <i className="ri-external-link-line text-xs"></i>
                  </a>
                </div>
              </div>
            )}

            {activeTab === 'pricing' && (
              <div>
                <h2 className="text-lg font-black text-gray-900 mb-2">Pricing</h2>
                <p className="text-sm text-gray-500 mb-6">{tool.quickInfo.pricing}</p>
                <div className="grid grid-cols-3 gap-4 mb-8">
                  {[
                    { tier: 'Starter', desc: 'Get started with basic access and standard features at entry-level pricing', color: 'border-gray-200 bg-white', badge: '', btnColor: 'bg-gray-900 text-white' },
                    { tier: 'Standard', desc: 'Most popular option combining comprehensive features with excellent value', color: 'border-slate-400 bg-slate-50', badge: 'Best Value', btnColor: 'bg-gradient-to-r from-slate-700 to-gray-800 text-white' },
                    { tier: 'Premium', desc: 'Full access to all features, priority support, and maximum performance', color: 'border-gray-200 bg-white', badge: '', btnColor: 'bg-gray-900 text-white' },
                  ].map((plan, i) => (
                    <div key={i} className={`rounded-2xl border-2 p-5 relative ${plan.color}`}>
                      {plan.badge && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-bold bg-slate-700 text-white px-3 py-0.5 rounded-full whitespace-nowrap">{plan.badge}</span>
                      )}
                      <h3 className="text-sm font-black text-gray-900 mb-2">{plan.tier}</h3>
                      <p className="text-xs text-gray-500 leading-relaxed mb-4">{plan.desc}</p>
                      <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className={`flex items-center justify-center gap-1.5 w-full text-xs font-semibold py-2.5 rounded-xl cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap ${plan.btnColor}`}>
                        Get Started <i className="ri-external-link-line text-xs"></i>
                      </a>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                  <p className="text-xs text-amber-800 flex items-start gap-2">
                    <i className="ri-information-line text-amber-600 text-sm mt-0.5 flex-shrink-0"></i>
                    Pricing may vary. Visit {tool.name}&apos;s official website for the most up-to-date pricing and plan details.
                  </p>
                </div>
              </div>
            )}

            {/* About Reviews */}
            <div className="mt-10 bg-gray-50 rounded-2xl p-5 border border-gray-100">
              <h3 className="text-sm font-black text-gray-800 mb-3">About These Reviews</h3>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">Reviews marked with &quot;Verified&quot; come from authenticated users who have actually used the service. We collect reviews from multiple trusted sources for balanced coverage.</p>
              <div className="flex items-center gap-3">
                {['Trustpilot', 'Google', 'Amazon', 'Reddit'].map((src, i) => (
                  <span key={i} className="text-xs font-semibold text-gray-500 bg-white border border-gray-200 rounded-lg px-2.5 py-1">{src}</span>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="col-span-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-4 sticky top-32" style={{ boxShadow: '0 1px 10px rgba(0,0,0,0.05)' }}>
              <h3 className="text-sm font-black text-gray-800 mb-4">Quick Info</h3>
              <div className="space-y-3">
                {[
                  { label: 'Company', value: tool.quickInfo.company, icon: 'ri-building-line' },
                  { label: 'Category', value: tool.quickInfo.category, icon: 'ri-car-line' },
                  { label: 'Rating', value: `${tool.rating}/5.0`, icon: 'ri-star-line' },
                  { label: 'Users', value: tool.quickInfo.users, icon: 'ri-user-line' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
                      <i className={`${item.icon} text-gray-500 text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-xs font-semibold text-gray-800">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tags */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-2">Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {tool.tags.map((tag, i) => (
                    <span key={i} className="text-xs font-medium text-slate-700 bg-slate-50 rounded-full px-2.5 py-0.5">{tag}</span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className={`mt-5 rounded-xl p-4 bg-gradient-to-br ${tool.logoGrad}`}>
                <p className="text-xs font-black text-white mb-1">Ready to start?</p>
                <p className="text-xs text-white/80 mb-3">Experience {tool.name} and see why thousands trust it daily.</p>
                <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center justify-center gap-1.5 w-full bg-white text-gray-900 text-xs font-bold py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">
                  Visit now <i className="ri-external-link-line text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Related Tools */}
        {relatedTools.length > 0 && (
          <div className="mt-12 pt-10 border-t border-gray-100">
            <h2 className="text-lg font-black text-gray-900 mb-6">Related services</h2>
            <div className="grid grid-cols-3 gap-5">
              {relatedTools.map((rt, i) => (
                <a key={i} href={`/categories/automotive-transportation/${rt.slug}`} className="group flex items-start gap-3 bg-white rounded-2xl border border-gray-100 p-4 hover:border-slate-200 transition-all cursor-pointer" style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
                  <BrandLogo logoUrl={rt.logoUrl} logoGrad={rt.logoGrad} logoIcon={rt.logoIcon} name={rt.name} size="sm" />
                  <div className="min-w-0">
                    <h4 className="text-sm font-black text-gray-900 group-hover:text-slate-700 transition-colors">{rt.name}</h4>
                    <p className="text-xs text-gray-400 truncate">{rt.tagline}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-xs font-semibold text-gray-600">{rt.rating}</span>
                      <span className="text-xs text-gray-400 ml-1 whitespace-nowrap">View details</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── FOOTER ── */}
      <SiteFooter />
    </div>
  );
}
